/**
 * Configuration for France-specific styling and features
 * This centralizes all France-specific customizations in one place
 *
 * Note: France-specific styling is applied only when BOTH conditions are met:
 * 1. Country is France
 * 2. Crop is Sunflower
 *
 * France shows the agronomic weighting list same as other countries
 */

import { isFrance } from 'utils/countryCode';

export interface FranceStyleConfig {
  // Whether to use the limited agronomic preferences layout (3 fields only)
  useLimitedAgronomicPreferences: boolean;

  // Whether to show the separate Product Traits section
  showProductTraitsSection: boolean;

  // Whether to use France-specific responsive layouts
  useCustomResponsiveLayouts: boolean;
}

/**
 * Determines if France-specific styling should be applied
 * @param countryCode - The country code to check
 * @param cropName - The crop name to check
 * @returns Configuration object for France-specific features
 */
export const getFranceStyleConfig = (
  countryCode: string | undefined,
  cropName?: string | undefined
): FranceStyleConfig => {
  const isFranceCountry = isFrance(countryCode);
  const isSunflowerCrop = cropName?.toLowerCase().includes('sunflower') ?? false;

  // Apply France-specific styling only for France AND sunflower crop
  const shouldApplyFranceFeatures = isFranceCountry && isSunflowerCrop;

  return {
    useLimitedAgronomicPreferences: shouldApplyFranceFeatures,
    showProductTraitsSection: shouldApplyFranceFeatures,
    useCustomResponsiveLayouts: shouldApplyFranceFeatures,
  };
};

/**
 * Check if any France-specific features are enabled
 * @param countryCode - The country code to check
 * @param cropName - The crop name to check
 * @returns True if France-specific features should be used
 */
export const shouldUseFranceFeatures = (
  countryCode: string | undefined,
  cropName?: string | undefined
): boolean => {
  const isFranceCountry = isFrance(countryCode);
  const isSunflowerCrop = cropName?.toLowerCase().includes('sunflower') ?? false;
  return isFranceCountry && isSunflowerCrop;
};
